import React, { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQGridProps {
  faqs: FAQItem[];
  fontFamily?: string;
  questionFontSize?: {
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
  answerFontSize?: {
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
}

const FAQGrid: React.FC<FAQGridProps> = ({
  faqs,
  fontFamily = 'Inter, sans-serif',
  questionFontSize = { desktop: 20, tablet: 18, mobile: 16 },
  answerFontSize = { desktop: 16, tablet: 15, mobile: 14 },
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleIndex = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Inline SVGs with bold stroke for + and -
  const PlusIcon = (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{ marginRight: 12, flexShrink: 0 }}
    >
      <path d='M12 5V19' stroke='#1e293b' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
      <path d='M5 12H19' stroke='#1e293b' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );

  const MinusIcon = (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{ marginRight: 12, flexShrink: 0 }}
    >
      <path d='M5 12H19' stroke='#3b82f6' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );

  return (
    <section
      style={{
        backgroundColor: '#f5f9ff',
        padding: '100px 20px',
        fontFamily,
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h2
          style={{
            textAlign: 'center',
            fontSize: 48,
            fontWeight: 800,
            color: '#0f172a',
            marginBottom: 64,
            letterSpacing: '-0.5px',
            fontFamily,
          }}
        >
          Frequently asked questions
        </h2>

        <div
          className='faq-grid-container'
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 24,
          }}
        >
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                onClick={() => toggleIndex(index)}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 16,
                  padding: 32,
                  boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease',
                  borderLeft: isOpen ? '4px solid #3b82f6' : '4px solid transparent',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  fontFamily,
                }}
              >
                {/* Question row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: isOpen ? 16 : 0,
                    transition: 'margin-bottom 0.3s ease',
                  }}
                >
                  {isOpen ? MinusIcon : PlusIcon}
                  <h3
                    style={{
                      fontSize: questionFontSize.desktop,
                      color: '#1e293b',
                      margin: 0,
                      lineHeight: 1.4,
                      fontFamily: 'GT-Walsheim-Pro-Bold',
                    }}
                    className='faq-question'
                  >
                    {faq.question}
                  </h3>
                </div>

                {/* Answer below */}
                <div
                  style={{
                    maxHeight: isOpen ? 300 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                  }}
                >
                  <p
                    style={{
                      color: '#475569',
                      fontSize: answerFontSize.desktop,
                      lineHeight: 1.6,
                      margin: 0,
                      fontFamily: 'GT-Walsheim-Pro-Regular',
                    }}
                    className='faq-answer'
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsive font sizes and grid adjustment */}
      <style>
        {`
        @media (min-width: 768px) {
          .faq-grid-container {
            grid-template-columns: 1fr 1fr !important;
            gap: 24px !important;
          }
          .faq-question {
            font-size: ${questionFontSize.tablet}px !important;
          }
          .faq-answer {
            font-size: ${answerFontSize.tablet}px !important;
          }
        }

        @media (max-width: 767px) {
          .faq-question {
            font-size: ${questionFontSize.mobile}px !important;
          }
          .faq-answer {
            font-size: ${answerFontSize.mobile}px !important;
          }
        }
      `}
      </style>
    </section>
  );
};

export default FAQGrid;
